import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ScrollHandler from './scroll-handler';

import { NAV_HEIGHT_WITH_MARGIN } from './constants';

import * as S from './styles';

type Tab = {
  id: string;
  title: string;
};

type OwnProps = {
  defaultActiveTabId: string;
  tabs: Tab[];
};

const StickyTabNavigation = ({ defaultActiveTabId, tabs }: OwnProps) => {
  const location = useLocation();

  const [_activeTab, _setActiveTab] = useState<string>(defaultActiveTabId);
  const [_scrollOngoing, _setScrollOngoing] = useState<boolean>(false);

  // Uses refs to hold states, to make them accessible in event listener callbacks
  const activeTab = useRef(_activeTab);
  const scrollOngoing = useRef(_scrollOngoing);

  const setActiveTab = useCallback(data => {
    activeTab.current = data;
    _setActiveTab(data);
  }, []);

  const setScrollOngoing = useCallback(data => {
    scrollOngoing.current = data;
    _setScrollOngoing(data);
  }, []);

  const handleScrollOngoingState = useCallback(
    element => {
      if (
        activeTab.current === element.id &&
        window.scrollY >= element.position &&
        window.scrollY <= element.positionEnd
      )
        setScrollOngoing(false);
    },
    [setScrollOngoing]
  );

  const handleActiveStateOnClick = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setScrollOngoing(true);
    },
    [setActiveTab, setScrollOngoing]
  );

  const handleActiveStateOnScroll = useCallback(() => {
    const tabElements = tabs
      .map(tab => {
        const element = document.getElementById(tab.id);
        const position = element
          ? element.offsetTop - NAV_HEIGHT_WITH_MARGIN
          : 0;

        return {
          element,
          position,
          id: tab.id,
        };
      })
      .map((element, index, array) => {
        return {
          ...element,
          positionEnd: array[index + 1]
            ? array[index + 1].position - 1
            : document.body.scrollHeight,
        };
      });

    tabElements.map(element => {
      if (!element.position) return null;

      // If there's an ongoing tab click scrollTo, do not change tab active state.
      if (scrollOngoing.current) {
        return handleScrollOngoingState(element);
      }
      // Sets active tab based on scroll position for manual scroll.
      if (
        activeTab.current !== element.id &&
        window.scrollY >= element.position
      ) {
        return setActiveTab(element.id);
      }

      return null;
    });
  }, [setActiveTab, handleScrollOngoingState, tabs]);

  const allTabs = tabs.map(tab => ({
    ...tab,
    active: tab.id === activeTab.current,
    to: `${location.pathname}${location.search}${tab.id}`,
  }));

  return (
    <>
      <ScrollHandler onScrollCallback={handleActiveStateOnScroll} />
      <S.Tabs onClick={handleActiveStateOnClick} tabs={allTabs} />
    </>
  );
};

export default StickyTabNavigation;
